import { useEffect, useState, useCallback, useMemo } from 'react'
import { fetchUserOrganizations } from '../services/userServices'
import { IOrganization } from '../components/Layout/Navbar/interfaces'

interface UseFetchOrganizationsProps {
    currentOrgId?: string
    excludeOrgs?: string[]
    includeVirtualOrgs?: boolean
}

const PAGE_SIZE = 30

export const useFetchOrganizations = ({
    currentOrgId,
    excludeOrgs,
    includeVirtualOrgs = false,
}: UseFetchOrganizationsProps): {
    orgsList: IOrganization[]
    isFetching: boolean
    hasMore: boolean
    onSearch: (_: string, text: string) => void
    fetchNextPage: () => void
} => {
    const [fetchedOrgs, setFetchedOrgs] = useState<any[]>([])
    const [pageNumber, setPageNumber] = useState<number>(1)
    const [searchText, setSearchText] = useState<string>('')
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const orgsList = useMemo(() => [...fetchedOrgs], [fetchedOrgs])
    useEffect(() => {
        // Ignore stale responses: this effect reruns on every searchText /
        // pageNumber change (e.g. the picker's initial "" fetch on open,
        // immediately followed by the user's typed search), with no
        // request-level cancellation of the previous call. Without this
        // guard, if the earlier (e.g. empty-search) request resolves AFTER
        // the newer (search-text) one, its now-stale, unfiltered page-1
        // response overwrites the correctly-filtered results — the org the
        // user just searched for silently disappears from the list.
        let cancelled = false
        const fetchRecords = async () => {
            setIsFetching(true)
            const orgsResponse = await fetchUserOrganizations({
                pageNumber,
                size: PAGE_SIZE,
                currentOrgId,
                searchText: searchText?.trim(),
                includeVirtualOrgs,
            })
            if (cancelled) return
            const orgs = orgsResponse?.organizations.filter(
                (i) => !excludeOrgs?.includes(i.id),
            )

            setFetchedOrgs((prev) =>
                pageNumber === 1 ? orgs : [...prev, ...orgs],
            )
            setHasMore(orgsResponse?.organizations?.length === PAGE_SIZE)
            setIsFetching(false)
        }
        fetchRecords()
        return () => {
            cancelled = true
        }
    }, [pageNumber, searchText, currentOrgId, excludeOrgs, includeVirtualOrgs])

    const onSearch = useCallback((_: string, text: string) => {
        setSearchText(text)
        setPageNumber(1)
    }, [])

    const fetchNextPage = useCallback(() => {
        if (!isFetching && hasMore) {
            setPageNumber((p) => p + 1)
        }
    }, [isFetching, hasMore])

    return {
        orgsList,
        isFetching,
        hasMore,
        onSearch,
        fetchNextPage,
    }
}
